/**
 * The nightly record.
 *
 * One tick per night. An eventful night is one where something changed and
 * we put it back, and it is drawn in ink rather than in the attention
 * colour, because nothing is being asked of the reader. See record.css for
 * the full reasoning; it is a rule, not a preference.
 *
 * `from` and `to` are plain-words labels the caller chooses; nothing here
 * checks that they actually span `nights.length` nights. That is the same
 * shape of risk as an unsourced number on a marketing page, just not one the
 * type system can refuse the way `Stat`'s required `from` does: whoever
 * assembles the data owns the correspondence between the dates and the count.
 */
import { useEffect, useRef } from 'react'
import { cn } from '@/utils/cn'
import './record.css'

export function Nights({
  nights,
  from,
  to,
  sample,
}: {
  /** One entry per night, oldest first. `true` means something changed and we put it back. */
  nights: boolean[]
  /** Plain-words date of the first night, e.g. "1 March". */
  from: string
  /** Plain-words date of the last night, e.g. "Last night". */
  to: string
  /** Marks the figures as synthetic. Required anywhere the data is not a real customer's. */
  sample?: boolean
}) {
  const lineRef = useRef<HTMLDivElement>(null)
  const eventfulCount = nights.filter(Boolean).length

  useEffect(() => {
    const line = lineRef.current
    if (!line) return
    // The right-hand label reads "Last night", so the last night is what has
    // to be on screen to start: on a narrow line that overflows, the reader
    // should scroll back into history, not forward toward a label that was
    // never in view. A no-op when the line does not overflow, guarded anyway
    // so the intent reads here rather than relying on scrollLeft clamping.
    // No smooth behaviour: this sets an initial position, not a motion.
    if (line.scrollWidth > line.clientWidth) {
      line.scrollLeft = line.scrollWidth
    }
  }, [nights.length])

  return (
    <div className="ds-nights">
      <div
        className="ds-nights__line"
        ref={lineRef}
        role="img"
        aria-label={`${nights.length} nights, ${eventfulCount} when something changed`}
      >
        {nights.map((eventful, i) => (
          <span
            // Position is the identity here: these are consecutive nights, not a set.
            // The line itself carries the announced label, so each tick stays
            // out of the accessibility tree rather than being read one by one.
            key={i}
            aria-hidden="true"
            className={cn('ds-nights__tick', eventful && 'ds-nights__tick--eventful')}
          />
        ))}
      </div>
      <div className="ds-nights__ends">
        <span>{from}</span>
        <span>{to}</span>
      </div>
      <div className="ds-nights__key">
        <span>
          <i className="ds-nights__sw" aria-hidden="true" />
          Nothing changed
        </span>
        <span>
          <i className="ds-nights__sw ds-nights__sw--eventful" aria-hidden="true" />
          Something changed, and we put it back
        </span>
      </div>
      {sample && (
        <p className="ds-sample" style={{ marginTop: 10 }}>
          Sample data for a fictional site.
        </p>
      )}
    </div>
  )
}
